// src/views/MainView.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  AppBar,
  Tab,
  Tabs,
  Container,
  Box,
  Paper,
  Grid,
} from "@mui/material";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";

import GeneralTab from "../components/general/clientOther";
import GeneralQuote from "../components/general/generalQuote";
import DriverInformationList from "../components/drivers/driverInformationList";
import VehicleInformationList from "../components/cars/VehicleInformationList";
import CompareQuotesTab from "../components/compareQuotes/comparision";

import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllVehicleInfo } from "../redux/actions/vehicleInfoActions";
import { getAllDriverInfo } from "../redux/actions/driverInfoActions";
import {
  getGeneralPartyInfoById,
  getGeneralPartyInfoById as getGeneralPartyInfoByIdAction,
} from "../redux/actions/generalPartyInfoActions";
import { convertToE164 } from "../common/helpers/commonFun";
import { border } from "polished";

const theme = createTheme();

const StyledTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTab-root": {
    minWidth: "unset",
    fontWeight: 600,
    padding: "25px 0",
    fontSize: "1rem",
    textTransform: "none",
    letterSpacing: "0.5px",
    fontFamily: '"Poppins", sans-serif',
    color: theme.palette.text.secondary,
    borderBottom: "3px solid transparent",
    transition: "border-bottom-color 0.3s",
    "&:hover": { borderBottomColor: theme.palette.text.secondary },
  },
  "& .Mui-selected": { color: theme.palette.text.secondary, borderBottom: "3px solid", borderBottomColor: theme.palette.text.primary },
  "& .MuiTabs-indicator": { display: "none" },
}));

function MainView() {
  // Tabs & step within General
  const [value, setValue] = useState(0); // active tab
  const [pageIndex, setPageIndex] = useState(0); // 0: General, 1: General Quote
  const [formData, setFormData] = useState({});
  const [visitedTabs, setVisitedTabs] = useState(new Array(6).fill(false));

  const dispatch = useDispatch();
  const location = useLocation();
  const { generalData, isNewQuote, clientCustomerId } = location.state || {};
  const id = generalData?.gpId;

  // Redux data
  const getGpId = useSelector((s) => s.generalPartyInfo.gpId);
  const generalPartyInfo = useSelector((s) => s.generalPartyInfo.allGeneralPartyInfo);
  const allVehicleInfo = useSelector((s) => s.vehicleInfo.allVehicleInfo);
  const allDriverInfo = useSelector((s) => s.driverInfo.allDriverInfo);

  const hasGeneralPartyInfo =
    generalPartyInfo &&
    (Array.isArray(generalPartyInfo)
      ? generalPartyInfo.length > 0
      : Object.keys(generalPartyInfo).length > 0);
  const hasDrivers = Array.isArray(allDriverInfo) && allDriverInfo.length > 0;
  const hasVehicles = Array.isArray(allVehicleInfo) && allVehicleInfo.length > 0;

  // Guard-capable refs (children implement shouldConfirm/saveWithoutNav/resetForm)
  const generalRef = useRef(null);
  const generalQuoteRef = useRef(null);
  const driversRef = useRef(null);
  const vehiclesRef = useRef(null);
  const compareRef = useRef(null); // optional if you add a guard there later

  // Confirmation modal (only for top-menu navigation)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingGuardReason, setPendingGuardReason] = useState("");
  const proceedFnRef = useRef(null);

  function getActiveGuardRef(tabIndex, idx) {
    if (tabIndex === 0) return idx === 0 ? generalRef : generalQuoteRef;
    if (tabIndex === 1) return driversRef;
    if (tabIndex === 2) return vehiclesRef;
    if (tabIndex === 3) return compareRef;
    return null;
  }

  const onStay = () => {
    setConfirmOpen(false);
    proceedFnRef.current = null;
  };

  const onResetWithoutSave = () => {
    const guard = getActiveGuardRef(value, pageIndex);
    guard?.current?.resetForm?.();
    const go = proceedFnRef.current;
    setConfirmOpen(false);
    proceedFnRef.current = null;
    if (typeof go === "function") go();
  };

  const onSaveAndContinue = async () => {
    const guard = getActiveGuardRef(value, pageIndex);
    const result = await guard?.current?.saveWithoutNav?.();
    if (result?.ok) {
      const go = proceedFnRef.current;
      setConfirmOpen(false);
      proceedFnRef.current = null;
      if (typeof go === "function") go();
    } else {
      setConfirmOpen(false);
    }
  };

  // Listen ONLY to main-menu navigation intents
  useEffect(() => {
    const maybeBlock = (ev) => {
      const { detail } = ev || {};
      if (!detail || detail.origin !== "mainMenu") return;

      const proceed = typeof detail.proceed === "function" ? detail.proceed : () => {};
      const cancel = typeof detail.cancel === "function" ? detail.cancel : () => {};

      // Cancel immediate navigation from menu
      cancel();

      let msg = "You have unsaved changes.";
      if (value === 0 && pageIndex === 0) msg = "You have filled all required fields on the General form.";
      if (value === 0 && pageIndex === 1) msg = "You have filled all required fields on the General Quote form.";
      if (value === 1) msg = "You have unsaved changes on the Drivers step.";
      if (value === 2) msg = "You have unsaved changes on the Cars step.";

      const guard = getActiveGuardRef(value, pageIndex);
      const shouldConfirm =
        !!guard?.current && typeof guard.current.shouldConfirm === "function"
          ? guard.current.shouldConfirm()
          : false;

      if (!shouldConfirm) {
        proceed();
        return;
      }

      proceedFnRef.current = proceed;
      setPendingGuardReason(msg);
      setConfirmOpen(true);
    };

    window.addEventListener("app:navigation-intent", maybeBlock);
    window.addEventListener("app:create-quote-intent", maybeBlock);
    return () => {
      window.removeEventListener("app:navigation-intent", maybeBlock);
      window.removeEventListener("app:create-quote-intent", maybeBlock);
    };
  }, [value, pageIndex]);

  // Reset state for “new quote”
  useEffect(() => {
    if (isNewQuote) {
      setValue(0);
      setPageIndex(0);
      setFormData({});
      setVisitedTabs(new Array(6).fill(false));
    }
  }, [isNewQuote]);

  // Load existing data
  useEffect(() => {
    if (id) {
      dispatch(getGeneralPartyInfoById(id));
      dispatch(getAllVehicleInfo(id));
      dispatch(getAllDriverInfo(id));
    }
  }, [dispatch, id]);

  // Top tabs change (internal – no modal)
  const handleChange = (_e, newValue) => {
    if (newValue === 1 && !hasGeneralPartyInfo) return;
    if (newValue === 2 && !hasDrivers) return;
    if (newValue === 3 && !hasVehicles) return;

    setValue(newValue);
    dispatch({ type: "RESET_ALL_RATES" });
    // when switching tabs, always reset pageIndex for General
    setPageIndex(0);
    setVisitedTabs((prev) => {
      const next = [...prev];
      next[newValue] = true;
      return next;
    });
  };

  // Explicit next/back logic: keep General’s two steps stable
  const handleNext = () => {
    if (value === 0) {
      // Inside General: pageIndex 0 -> 1, then move to Drivers
      if (pageIndex === 0) {
        setPageIndex(1);
        return;
      }
      // moving out of General to Drivers
      if (!hasGeneralPartyInfo) return;
      setValue(1);
      setPageIndex(0);
      return;
    }

    // Other tabs: move to next tab if allowed
    if (value === 1) {
      if (!hasDrivers) return;
      setValue(2);
      setPageIndex(0);
      return;
    }
    if (value === 2) {
      if (!hasVehicles) return;
      setValue(3);
      setPageIndex(0);
      return;
    }
    // value === 3: Comparison -> no further step
  };

  const handleBack = () => {
    dispatch(getGeneralPartyInfoByIdAction(getGpId));

    if (value === 0) {
      // Inside General: step 2 -> step 1
      if (pageIndex === 1) {
        setPageIndex(0);
        return;
      }
      // Already at first page of first tab: nothing to do
      return;
    }

    // From Drivers -> back to General step 2
    if (value === 1) {
      setValue(0);
      setPageIndex(1);
      return;
    }
    // From Cars -> back to Drivers
    if (value === 2) {
      setValue(1);
      setPageIndex(0);
      return;
    }
    // From Comparison -> back to Cars
    if (value === 3) {
      setValue(2);
      setPageIndex(0);
      return;
    }
  };

  // General → General Quote data handoff
  const handleSubmitGeneralTab = (data) => {
    const formatted = { ...data, cellPhone: convertToE164(data.cellPhone) };
    setFormData(formatted);
    handleNext(); // advances to step 2 explicitly
  };

  // Render pages
  const vehicleData = {};
  const formPages = [
    [
      <GeneralTab
        key="general"
        ref={generalRef}
        handleNext={handleNext}
        handleSubmitGeneralTab={handleSubmitGeneralTab}
        id={id}
        generalData={generalData}
        customerId={clientCustomerId}
      />,
      <GeneralQuote
        key="general-quote"
        ref={generalQuoteRef}
        handleNext={handleNext}
        handleBack={handleBack}
        data={formData}
        id={id}
      />,
    ],
    [
      <DriverInformationList
        key="drivers"
        ref={driversRef}
        handleNext={handleNext}
        handleBack={handleBack}
        id={id}
      />,
    ],
    [
      <VehicleInformationList
        key="vehicles"
        ref={vehiclesRef}
        handleNext={handleNext}
        handleBack={handleBack}
        vehicleData={vehicleData}
        id={id}
      />,
    ],
    [
      <CompareQuotesTab
        key="comparison"
        // If CompareQuotesTab exposes a guard API later, pass: ref={compareRef}
        handleNext={handleNext}
        handleBack={handleBack}
        id={id}
      />,
    ],
  ];

  return (
    <Box>
      <AppBar position="static" color="default">
        <Container>
          <StyledTabs value={value} onChange={handleChange} variant="fullWidth">
            <Tab id="General" label="General" />
            <Tab
              id="Drivers"
              label="Drivers"
              disabled={!hasGeneralPartyInfo}
              sx={{ opacity: hasGeneralPartyInfo ? 1 : 0.5, cursor: hasGeneralPartyInfo ? "pointer" : "not-allowed" }}
            />
            <Tab
              id="Cars"
              label="Cars"
              disabled={!hasDrivers}
              sx={{ opacity: hasDrivers ? 1 : 0.5, cursor: hasDrivers ? "pointer" : "not-allowed" }}
            />
            <Tab
              id="Comparison"
              label="Comparison"
              disabled={!hasVehicles}
              sx={{ opacity: hasVehicles ? 1 : 0.5, cursor: hasVehicles ? "pointer" : "not-allowed" }}
            />
          </StyledTabs>
        </Container>
      </AppBar>

      <Paper sx={{ p: 2, margin: "0 auto", boxShadow: "none" }}>
        <Grid item xs={12} sx={{ margin: "0 auto" }}>
          {formPages[value][pageIndex]}
        </Grid>
      </Paper>

      <Dialog open={confirmOpen} onClose={onStay}  fullWidth>
        <DialogTitle
          className="btn-primary"
          sx={{
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
          }}
        >
          Unsaved changes
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {pendingGuardReason} Do you want to:
          </Typography>
          <ul style={{ marginTop: 8 }}>
            <li><b>Stay</b> on this page and keep editing.</li>
            <li><b>Save &amp; Continue</b> to save your changes, then continue.</li>
            <li><b>Reset without save</b> to discard changes and continue.</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={onStay} variant="text">Stay</Button>
          <Button onClick={onResetWithoutSave} variant="outlined">
            Reset without save
          </Button>
          <Button onClick={onSaveAndContinue} variant="outlined">
            Save &amp; Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MainView;
